import React, { useState } from "react";
import { X } from "lucide-react";
import { EMAIL_PATTERN } from "../../lib/fieldFormat";

export interface EmailChipsProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  /**
   * Whether the box takes focus on mount. On for a dialog whose whole question
   * is this field; off where something above it should be read first.
   */
  autoFocus?: boolean;
}

/**
 * A list of addresses as chips, with a box to type more into.
 *
 * A plain text field would do for the one address a dialog opens on, but a
 * plan is often copied to a spouse or a second inbox, and a comma-separated
 * line gives no way to see that an address was accepted or to take one back
 * without retyping the rest.
 *
 * Shared rather than private to the meal plan's send dialog, which is where it
 * was written: the exercise plan is sent the same way, to the same kind of
 * list, and two copies of an address field would be two places for the
 * validation to drift.
 */
const EmailChips: React.FC<EmailChipsProps> = ({
  emails,
  onChange,
  autoFocus = true,
}) => {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  /** Takes what has been typed, if it is an address the list does not hold. */
  const commit = () => {
    const value = text.trim().replace(/,$/, "");
    if (!value) return;

    if (!EMAIL_PATTERN.test(value)) {
      setError("Enter a valid email address");
      return;
    }
    if (!emails.includes(value)) onChange([...emails, value]);
    setText("");
    setError("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
      return;
    }
    // Backspace on an empty box takes the last chip back, which is what every
    // other chip field does and what fingers expect.
    if (event.key === "Backspace" && !text && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div>
      {/* The box lights up on focus within, so the caret in the input reads
          the same as focus on any other field in the app. */}
      <div
        className={`flex min-h-[46px] flex-wrap items-center gap-2 rounded-[6px] border bg-white px-2.5 py-2 transition-colors ${
          error
            ? "border-red-400 focus-within:border-red-500"
            : "border-line focus-within:border-brand"
        }`}
      >
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1.5 rounded-[4px] border-[0.5px] border-[#88E2ED] bg-[#EFFBFD] px-2 py-1 text-[13px] text-ink"
          >
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              onClick={() => onChange(emails.filter((one) => one !== email))}
              className="text-ink-subtle transition-colors hover:text-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}

        <input
          type="email"
          value={text}
          aria-label="Add an email address"
          // Only while the box is bare — once a chip is in it, the field has
          // shown what it wants.
          placeholder={emails.length === 0 ? "name@example.com" : ""}
          autoFocus={autoFocus}
          onChange={(event) => {
            setText(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={handleKeyDown}
          // Leaving the box takes what is in it, so an address typed and left
          // unconfirmed is not silently dropped on Send.
          onBlur={commit}
          className="min-w-[140px] flex-1 bg-transparent text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none"
        />
      </div>
      {error && (
        <p className="mt-1.5 text-[11px] 3xl:text-[12px] text-red-500">{error}</p>
      )}
    </div>
  );
};

export default EmailChips;
